import React, { useState, useRef, useEffect } from 'react';
import { Bell, X, Check, Eye, EyeOff, Trash2, ArrowRight, CheckCircle } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const NotificationIcon: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, markAsRead, markAsUnread, deleteNotification } = useNotifications();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNotificationClick = async (notification: any) => {
    try {
      if (!notification.is_read) {
        try {
          await markAsRead(notification.id);
        } catch (error) {
          console.warn('Warning: Could not mark notification as read:', error);
        }
      }
      if (notification.action_url) {
        navigate(notification.action_url);
      } else {
        switch (notification.entity_type) {
          case 'production': navigate('/productions'); break;
          case 'sample': navigate('/samples'); break;
          case 'express': navigate('/express'); break;
          case 'express_sample': navigate('/expresssamples'); break;
          default: navigate('/notifications');
        }
      }
    } catch (error) {
      console.error('Error in handleNotificationClick:', error);
      navigate('/notifications');
    }
    setIsOpen(false);
  };

  const toggleReadStatus = async (notification: any) => {
    try {
      if (notification.is_read) {
        await markAsUnread(notification.id);
      } else {
        await markAsRead(notification.id);
      }
    } catch (error) {
      console.error('Error toggling read status:', error);
    }
  };

  const handleDelete = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await deleteNotification(notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.is_read);
      for (const notification of unreadNotifications) {
        await markAsRead(notification.id);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy HH:mm', { locale: es });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'production': return '🏭';
      case 'sample': return '🧪';
      case 'express': return '⚡';
      case 'express_sample': return '🔬';
      default: return '📢';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors"
        title="Notificaciones"
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 z-50 max-h-[500px] overflow-hidden">
          <div className="max-h-[360px] overflow-y-auto">
            {notifications.filter(notification => !notification.is_read).length === 0 ? (
              <div className="p-8 text-center">
                <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                  <Bell className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 font-medium">No hay notificaciones nuevas</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Todas las notificaciones han sido leídas</p>
              </div>
            ) : (
              notifications
                .filter(notification => !notification.is_read)
                .slice(0, 10)
                .map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-5 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      !notification.is_read ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-xl flex-shrink-0 ${
                        notification.type === 'production' ? 'bg-green-100 dark:bg-green-900/30' :
                        notification.type === 'sample' ? 'bg-purple-100 dark:bg-purple-900/30' :
                        notification.type === 'express' ? 'bg-orange-100 dark:bg-orange-900/30' :
                        notification.type === 'express_sample' ? 'bg-pink-100 dark:bg-pink-900/30' :
                        'bg-blue-100 dark:bg-blue-900/30'
                      }`}>
                        <span className="text-xl">{getNotificationIcon(notification.type)}</span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-semibold mb-2 text-sm ${
                          !notification.is_read 
                            ? 'text-gray-900 dark:text-white' 
                            : 'text-gray-700 dark:text-gray-300'
                        }`}>
                          {notification.title}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 leading-relaxed">
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                            {formatDate(notification.created_at)}
                          </span>
                          
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleReadStatus(notification);
                              }}
                              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title={notification.is_read ? 'Marcar como no leída' : 'Marcar como leída'}
                            >
                              {notification.is_read ? <EyeOff className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(notification.id, e);
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>

          {/* Footer con botones mejorados */}
          <div className="p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between">
                              <button
                  onClick={() => navigate('/notifications')}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200"
                >
                <ArrowRight className="h-4 w-4 mr-2" />
                Ver todas
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all duration-200"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Marcar como leídas
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationIcon;